import React from 'react';
import { useState, useEffect } from 'react';

// MUI
import Button from '@mui/material/Button';
import { Alert, CircularProgress, Drawer, Grid, Box, Typography } from '@mui/material';

// Icons
import SkipNextIcon from '@mui/icons-material/SkipNext';
import { Close, ErrorRounded, PlayCircle, Autorenew, ArrowBack } from '@mui/icons-material';
import ScienceIcon from '@mui/icons-material/Science';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import IframeModal from '../Component/IframeModal';

// Components
import PanelHeader from '../common/PanelHeader';

// Styles
import './TestPanel.css';

// Util
import { Link } from 'react-router-dom';
import TestIframeRender from './TestIframeRender';
import { send_request } from '../../utils/Request';
import { useLocation, useNavigate } from 'react-router';
import AfterRunAlert from '../pages/Canvas/ForceStart/AfterRunAlert';
import ComponentNameFromId from '../ProjectCanvas/ComponentNameFromId';
import InputPlaceholdersBox from '../pages/Canvas/ForceStart/InputPlaceholdersBox';
import TriggerStartComponent from '../pages/Canvas/ForceStart/TriggerComponentUtil/TriggerStartComponent';
import ApiInstructions from '../pages/Canvas/ForceStart/ApiInstructions';
import {
  BlackButton,
  CustomGrid,
  DownArrow,
  ItemBox,
  RunIcon,
  Text,
  TriggerDescription,
  TriggerTitle,
} from '../pages/Canvas/ForceStart/CommonStyledComponents';
import config from '../../config';
import {
  AFTER_RUN_ALERT_TYPE,
  INPUT_DATA_LOCATION_TYPES,
} from '../pages/Canvas/ForceStart/ForceStartConstants';

const TestPanel = props => {
  const isTrigger = checkIfTrigger(props.compData);
  const isFormOrBulkAssessment = checkIfFormTrigger(props.compData);
  const navigate = useNavigate();

  // Control visibility of "Run via API" option in test mode
  const SHOW_API_OPTION_IN_TEST = false;

  const search = useLocation().search;
  const queryParams = new URLSearchParams(search);

  const [tests, setTests] = useState([]);
  const [placeholders, setPlaceholders] = useState(null);
  const [values, setValues] = useState(null);
  const [inputDataLocation, setInputDataLocation] = useState(
    INPUT_DATA_LOCATION_TYPES.MANUALLY_SET
  );
  const [isLoading, setIsLoading] = useState(false);

  const [showAfterRunAlert, setShowAfterRunAlert] = useState(null);
  const [successSessionId, setSuccessSessionId] = useState(null);

  const [step, setStep] = useState(0);
  const [refresh, setRefresh] = useState(false);
  const [loadingInputPHs, setLoadingInputPHs] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  const [selected, setSelected] = useState([]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    params.append('force_start', props.componentId);

    // In relation to the test, if it's a form or bulk assessment
    // Set to step 1 (trigger selection menu)
    if (isFormOrBulkAssessment && selected?.length === 0) setStep(1);
    else setStep(2);

    navigate({ search: params.toString() });
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      if (!props.executeTest) return;
      if (!isFormOrBulkAssessment || isLoading) {
        // skip force start in case of a form or excel sheet as it should be triggered on clicking the submit button.
        await executeForceStartTest();
      }
      props.setExecuteTest(false);
    };
    fetchData();
  }, [props.executeTest]);

  // Gets the UI to display related to the given step
  const getCurrentStep = () => {
    switch (step) {
      case 1:
        return getTriggerFormPanel();
      case 2:
        return getForceStartTestContent();
      case 3:
        return getTriggerContent();
      default:
        return <></>;
    }
  };

  // Checks if a given component is a trigger
  function checkIfTrigger(compData) {
    try {
      return !compData.data.hasInput;
    } catch (e) {
      console.log(e);
      return true;
    }
  }

  // Checks if a given component is a form/bulk assessment or api polling trigger
  // These trigger types have their own dedicated test UI
  function checkIfFormTrigger(compData) {
    try {
      let comp = compData.data;
      return (
        !comp.hasInput &&
        (comp.type === 'form' ||
          comp.type === 'bulk_assessment' ||
          comp.type === 'trigger_from_api_polling')
      );
    } catch (e) {
      console.log(e);
      return true;
    }
  }

  // Function for handling running of the force start test session
  const handleForceStartTest = async () => {
    if (props.isOpenWithComponent) {
      setIsLoading(true);
      await props.save();
    } else {
      await executeForceStartTest();
    }
  };

  const wait = ms =>
    new Promise(res => {
      return setTimeout(res, ms);
    });

  const executeForceStartTest = async () => {
    setIsLoading(true);

    await wait(1000); // Wait 1 second

    let body = {
      inputtedPlaceholders: values,
      projectId: props.projectId,
      componentId: props.componentId,
      componentType: props.compData.data ? props.compData.data.type : '',
      versionToRun: props.version,
      sessionIdsToExecuteWorkflow: selected,
      isTest: true,
    };

    const url = `project-service/project-session/force_start_workflow`;
    let json = await send_request(url, body, null, 'POST');

    if (json && json.data) {
      setSuccessSessionId(Object.values(json.data));
      setShowAfterRunAlert(AFTER_RUN_ALERT_TYPE.SUCCESS);
    } else {
      // Unsuccessful force-start
      setShowAfterRunAlert(AFTER_RUN_ALERT_TYPE.ERROR);
    }

    setIsLoading(false);
  };

  // Add this function to handle opening the modal
  const handleOpenModal = () => {
    setModalOpen(true);
  };

  // Add this function to handle closing the modal
  const handleCloseModal = () => {
    setModalOpen(false);
  };

  // Modify the getTriggerContent function
  const getTriggerContent = () => {
    // Determine the iframe URL based on component type
    const getIframeUrl = () => {
      const baseUrl = `${config.FORM.FORM_FRONT_END}`;
      const testId = tests[0]?.testId;

      if (props.compData.data?.type === 'bulk_assessment') {
        return `${baseUrl}/bulk/bulk_test/${props.componentId}/${testId}?test=true`;
      }
      return `${baseUrl}/form/form_test/${props.componentId}/${testId}?isLatestDraft=true`;
    };

    return (
      <>
        <div>
          <Alert className="test-alert" icon={<ErrorRounded className="test-alert-icon" />}>
            After submitting a test trigger, go to the{' '}
            <Link
              className="test-alert-link"
              to={`/project/logs/${props.projectId}?test=true`}
              target="_blank"
            >
              test session logs
            </Link>{' '}
            to track the test session
          </Alert>
        </div>
        <div>
          <Button
            className="browser-preview-btn"
            size="small"
            startIcon={<OpenInNewIcon />}
            onClick={handleOpenModal}
          >
            Browser preview
          </Button>
        </div>
        <div>
          <TestIframeRender
            version={props.version}
            projectId={props.projectId}
            componentId={props.componentId}
            compData={props.compData}
            tests={tests}
            setTests={setTests}
            isTestPane={true}
            refresh={refresh}
            setRefresh={setRefresh}
            loadingInputPHs={loadingInputPHs}
            setLoadingInputPHs={setLoadingInputPHs}
          />
        </div>
        <IframeModal open={modalOpen} onClose={handleCloseModal} url={getIframeUrl()} />
      </>
    );
  };

  // Gets the standard input placeholder UI
  const getForceStartTestContent = () => {
    const TEST_RUN_BUTTON_TEXT =
      selected.length > 1 ? `Test Run ${selected.length} Selected Sessions` : 'Test Run';

    const isApiMode = inputDataLocation === INPUT_DATA_LOCATION_TYPES.RUN_VIA_API;

    return (
      <>
        <ItemBox>
          <InputPlaceholdersBox
            placeholders={placeholders}
            setPlaceholders={setPlaceholders}
            values={values}
            setValues={setValues}
            setIsLoading={setIsLoading}
            projectId={props.projectId}
            componentId={props.componentId}
            version={props.version}
            inputDataLocation={inputDataLocation}
            setInputDataLocation={setInputDataLocation}
            queryParams={queryParams}
            isTrigger={isTrigger}
            isTest={true}
            step={step}
            fetchTestOutput={props.fetchTestOutput}
            setFetchTestOutput={props.setFetchTestOutput}
            loadingInputPHs={loadingInputPHs}
            setLoadingInputPHs={setLoadingInputPHs}
            selected={selected}
            setSelected={setSelected}
            SHOW_API_OPTION_IN_TEST={SHOW_API_OPTION_IN_TEST}
          />
        </ItemBox>
        <ItemBox>
          <Grid container gap={'16px'}>
            <CustomGrid item xs={12} vertical="center" horizontal="flex-start">
              <RunIcon />
              <Text fontWeight="bold">
                {isApiMode ? 'Test workflow via API' : `Start test run from ${props.componentId}`}
              </Text>
            </CustomGrid>
            {isApiMode ? (
              <Grid item xs={12}>
                <ApiInstructions isTest={true} />
              </Grid>
            ) : (
              <>
                <Grid item xs={12}>
                  <Text>
                    You are starting a test run from{' '}
                    <b>
                      <ComponentNameFromId componentId={props.componentId} />
                    </b>{' '}
                    onwards.
                  </Text>
                </Grid>
                <Grid item xs={12}>
                  <Grid
                    container
                    columnGap={2}
                    display="flex"
                    alignItems="center"
                    justifyContent="flex-end"
                  >
                    <Grid item>
                      <Button
                        variant="contained"
                        color="primary"
                        className="test-start-btn"
                        onClick={handleForceStartTest}
                        disabled={isLoading || !placeholders || !props.childTemplateLoaded}
                        startIcon={isLoading ? <CircularProgress size={24} /> : <PlayCircle />}
                      >
                        {isLoading ? 'Starting session...' : TEST_RUN_BUTTON_TEXT}
                      </Button>
                    </Grid>
                  </Grid>
                </Grid>
              </>
            )}
            {showAfterRunAlert && (
              <Grid item xs={12}>
                <AfterRunAlert
                  alertType={showAfterRunAlert}
                  sessionId={successSessionId}
                  projectId={props.projectId}
                  isLoading={isLoading}
                  isTest={true}
                />
              </Grid>
            )}
          </Grid>
        </ItemBox>
      </>
    );
  };

  // Gets the selection panel for the form and bulk assessment triggers
  const getTriggerFormPanel = () => {
    return (
      <>
        <ItemBox>
          <TriggerStartComponent
            component={{...props.compData.data, componentId: props.componentId}}
            BlackButton={BlackButton}
            TriggerTitle={TriggerTitle}
            TriggerDescription={TriggerDescription}
            version={props.version}
            isTest={true}
            handleTestOpen={async () => {
              await props.save();
              setStep(3);
            }}
            save={props.save}
            disabled={isLoading || !props.childTemplateLoaded}
            projectId={props.projectId}
          />
        </ItemBox>
        <div style={{ display: 'flex', justifyContent: 'center', padding: '4px 0' }}>
          <b>OR</b>
        </div>
        <ItemBox>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TriggerTitle>Manually trigger this component</TriggerTitle>
            </Grid>
            <Grid item xs={12}>
              <TriggerDescription>
                You can manually trigger this component specifying its output values
              </TriggerDescription>
            </Grid>
            <Grid item xs={12}>
              <BlackButton
                variant="contained"
                onClick={() => setStep(2)}
                startIcon={<SkipNextIcon />}
              >
                Manually Trigger
              </BlackButton>
            </Grid>
          </Grid>
        </ItemBox>
      </>
    );
  };

  const handleFormRefresh = async () => {
    if (step === 2) {
      // This is the force-start panel
      setLoadingInputPHs(true);
      await props.save();
      await wait(1000);
      props.setFetchTestOutput(true);
    } else if (step === 3) {
      // This is the iframe
      setLoadingInputPHs(true);
      await props.save();
      await wait(1000); // Wait 1 second
      setRefresh(true);
    }
  };

  const handleBackToStart = () => {
    setStep(isFormOrBulkAssessment ? 1 : 2);
    setShowAfterRunAlert(null);
    setSuccessSessionId(null);
  };

  return (
    <Drawer
      open={props.open}
      anchor="right"
      hideBackdrop={true}
      variant="permanent"
      onClose={props.handleClose}
      PaperProps={{
        className: 'test-panel-drawer',
        sx: {
          width: config.TEST_PANE_WIDTH,
          right: props.aiBuilderOpen ? config.AI_COMPONENT_BUILDER_PANE_WIDTH : '0',
          height: '100%',
          maxHeight: '100%',
          zIndex: 1001,
        },
      }}
    >
      <PanelHeader
        title={
          <Box className="test-panel-title-section">
            <ScienceIcon className="test-icon" />
            <Typography className="test-title">Test</Typography>
            <Typography className="test-version">Running version {props.version}</Typography>
          </Box>
        }
        showBackButton={step === 2 && isFormOrBulkAssessment}
        showExpandButton={false}
        showCloseButton={true}
        isExpanded={props.isExpanded}
        onBack={handleBackToStart}
        onExpand={props.handleExpand}
        onClose={props.handleClose}
      >
        {step > 1 && isFormOrBulkAssessment && (
          <Button
            className="refresh-button"
            size="small"
            startIcon={<Autorenew />}
            onClick={() => handleFormRefresh()}
          >
            Refresh
          </Button>
        )}
      </PanelHeader>
      <div className="test-panel-container">{getCurrentStep()}</div>
    </Drawer>
  );
};

export default TestPanel;
